import {
	type QpNotice,
	focusVisible,
	subscribeNotices,
} from "@quickpoint.me/shared";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/** Renders the shared notify() queue — the styled replacement for
 *  window.alert(). Announces via role="alert", auto-dismisses, manual ✕.
 *  Chrome UI: fixed accessible colors (AAA), not slide theming. */

const StyledNotices = styled.div`
	position: fixed;
	top: 4.5rem;
	left: 50%;
	transform: translateX(-50%);
	z-index: 5000;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	width: min(92vw, 34rem);
	pointer-events: none;
`;

const StyledNotice = styled.div<{ $variant: "error" | "info" }>`
	pointer-events: auto;
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	background: #fff;
	color: #111;
	border: 1px solid ${(p) => (p.$variant === "error" ? "#992f29" : "#5335c9")};
	border-left-width: 5px;
	border-radius: 8px;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
	padding: 0.8rem 0.9rem;
	font-family: "Gill Sans", "Trebuchet MS", sans-serif;
	font-size: 0.95rem;
	line-height: 1.45;

	p {
		margin: 0;
		flex: 1;
	}

	button {
		background: none;
		border: none;
		cursor: pointer;
		color: #4f4f4f;
		font-size: 1.1rem;
		line-height: 1;
		padding: 0.35rem 0.5rem;
		margin: -0.35rem -0.4rem -0.35rem 0;
		border-radius: 6px;
		${focusVisible}

		&:hover {
			color: #111;
			background: #f0eefb;
		}
	}
`;

export function NoticeHost() {
	const [notices, setNotices] = useState<QpNotice[]>([]);
	const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

	useEffect(() => {
		const currentTimers = timers.current;
		const unsubscribe = subscribeNotices((n) => {
			setNotices((prev) => [...prev, n]);
			currentTimers.set(
				n.id,
				setTimeout(() => {
					setNotices((prev) => prev.filter((x) => x.id !== n.id));
					currentTimers.delete(n.id);
				}, n.duration),
			);
		});
		return () => {
			unsubscribe();
			for (const t of currentTimers.values()) clearTimeout(t);
			currentTimers.clear();
		};
	}, []);

	if (notices.length === 0) return null;
	return (
		<StyledNotices>
			{notices.map((n) => (
				<StyledNotice key={n.id} $variant={n.variant} role="alert">
					<p>{n.message}</p>
					<button
						type="button"
						aria-label="Dismiss"
						onClick={() =>
							setNotices((prev) => prev.filter((x) => x.id !== n.id))
						}
					>
						✕
					</button>
				</StyledNotice>
			))}
		</StyledNotices>
	);
}
